import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE,
  FIRE_SIGHT_SIMULATION_LAYER_DEFINITIONS,
  FIRE_SIGHT_SIMULATION_REPORT_PRODUCT_CODE,
} from '../../../constants/fire-sight.constants';
import {
  IFireSightLayerOption,
  IFireSightMapViewModel,
  IFireSightMonitoringViewModel,
  IFireSightSimulationViewModel,
} from '../../../interfaces/fire-sight/IFireSightModels';
import { IFireSightState } from '../../../interfaces/fire-sight/IFireSightState';
import { hasFireSightFireObject } from '../../../utils/fire-sight/fire-sight-fire-object.util';
import { FIRE_SIGHT_FEATURE_KEY } from '../../reducers/fire-sight/fire-sight.reducer';

export const selectFireSightState =
  createFeatureSelector<IFireSightState>(FIRE_SIGHT_FEATURE_KEY);

export const selectFireSightProjectUuid = createSelector(
  selectFireSightState,
  (state) => state.projectUuid,
);
export const selectFireSightActiveView = createSelector(
  selectFireSightState,
  (state) => state.activeView,
);
export const selectFireSightBootstrap = createSelector(
  selectFireSightState,
  (state) => state.bootstrap,
);
export const selectFireSightMonitoringAreas = createSelector(
  selectFireSightState,
  (state) => state.monitoringAreas,
);
export const selectFireSightIncidents = createSelector(
  selectFireSightState,
  (state) => state.incidents,
);
export const selectFireSightHistoricalIncidents = createSelector(
  selectFireSightState,
  (state) => state.historicalIncidents,
);
export const selectFireSightOverviewView = createSelector(
  selectFireSightState,
  (state) => state.overviewView,
);
export const selectFireSightHistoryDateFilter = createSelector(
  selectFireSightState,
  (state) => state.historyDateFilter,
);
export const selectFireSightHistoryDisplayNameFilter = createSelector(
  selectFireSightState,
  (state) => state.historyDisplayNameFilter,
);
export const selectFireSightHistoryCentroidsVisible = createSelector(
  selectFireSightState,
  (state) => state.historyCentroidsVisible,
);
export const selectFireSightHistoricalIncidentsNextCursor = createSelector(
  selectFireSightState,
  (state) => state.historicalIncidentsNextCursor,
);
export const selectFireSightHistoricalIncidentsHasMore = createSelector(
  selectFireSightState,
  (state) => state.historicalIncidentsHasMore,
);
export const selectFireSightHistoryQueryKey = createSelector(
  selectFireSightState,
  (state) => state.historyQueryKey,
);
export const selectFireSightDisplayedIncidents = createSelector(
  selectFireSightIncidents,
  selectFireSightHistoricalIncidents,
  selectFireSightOverviewView,
  (incidents, historicalIncidents, overviewView) =>
    overviewView === 'history' ? historicalIncidents : incidents,
);
export const selectFireSightIncidentDetail = createSelector(
  selectFireSightState,
  (state) => state.incidentDetail,
);
export const selectFireSightObservations = createSelector(
  selectFireSightState,
  (state) => state.observations,
);
export const selectFireSightSimulations = createSelector(
  selectFireSightState,
  (state) => state.simulations,
);
export const selectFireSightSelectedIncidentUuid = createSelector(
  selectFireSightState,
  (state) => state.selectedIncidentUuid,
);
export const selectFireSightSelectedObservationUuid = createSelector(
  selectFireSightState,
  (state) => state.selectedObservationUuid,
);
export const selectFireSightSelectedSimulationUuid = createSelector(
  selectFireSightState,
  (state) => state.selectedSimulationUuid,
);
export const selectFireSightSelectedHorizonH = createSelector(
  selectFireSightState,
  (state) => state.selectedHorizonH,
);
export const selectFireSightSelectedProductCode = createSelector(
  selectFireSightState,
  (state) => state.selectedProductCode,
);
export const selectFireSightLoading = createSelector(
  selectFireSightState,
  (state) => state.loading,
);
export const selectFireSightErrors = createSelector(
  selectFireSightState,
  (state) => state.errors,
);
export const selectFireSightWebSocketStatus = createSelector(
  selectFireSightState,
  (state) => state.websocketStatus,
);
export const selectFireSightAllowedHorizons = createSelector(
  selectFireSightBootstrap,
  (bootstrap) => bootstrap?.capabilities.allowed_simulation_horizons_h ?? [6, 12, 24],
);

export const selectFireSightSelectedObservation = createSelector(
  selectFireSightObservations,
  selectFireSightSelectedObservationUuid,
  (observations, selectedUuid) =>
    observations.find((observation) => observation.uuid === selectedUuid) ?? null,
);

export const selectFireSightSelectedSimulation = createSelector(
  selectFireSightSimulations,
  selectFireSightSelectedSimulationUuid,
  (simulations, selectedUuid) =>
    simulations.find((simulation) => simulation.uuid === selectedUuid) ?? null,
);

export const selectFireSightSimulationAvailable = createSelector(
  selectFireSightSelectedSimulation,
  (simulation) => simulation !== null,
);

export const selectFireSightSelectedSimulationReportAsset = createSelector(
  selectFireSightSelectedSimulation,
  (simulation) =>
    simulation?.assets.find(
      (asset) =>
        asset.product_code === FIRE_SIGHT_SIMULATION_REPORT_PRODUCT_CODE,
    ) ?? null,
);

export const selectFireSightMonitoringViewModel = createSelector(
  selectFireSightIncidentDetail,
  selectFireSightSelectedObservation,
  (incident, observation): IFireSightMonitoringViewModel | null => {
    if (!incident) {
      return null;
    }
    const canStartSimulation = hasFireSightFireObject(observation);
    return { incident, observation, canStartSimulation };
  },
);

export const selectFireSightSimulationViewModel = createSelector(
  selectFireSightIncidentDetail,
  selectFireSightSelectedSimulation,
  selectFireSightSelectedHorizonH,
  selectFireSightSelectedProductCode,
  (incident, simulation, horizonH, productCode): IFireSightSimulationViewModel | null => {
    if (!incident || !simulation) {
      return null;
    }

    const selectedProductCode =
      productCode && simulation.assets.some((asset) => asset.product_code === productCode)
        ? productCode
        : simulation.assets.some(
              (asset) => asset.product_code === FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE,
            )
          ? FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE
          : (simulation.assets.find((asset) =>
              FIRE_SIGHT_SIMULATION_LAYER_DEFINITIONS.some(
                (definition) => definition.productCode === asset.product_code,
              ),
            )?.product_code ?? null);

    const layerOptions: IFireSightLayerOption[] =
      FIRE_SIGHT_SIMULATION_LAYER_DEFINITIONS.map((definition) => ({
        ...definition,
        available: simulation.assets.some(
          (asset) => asset.product_code === definition.productCode,
        ),
        selected: definition.productCode === selectedProductCode,
      }));

    return {
      incident,
      simulation,
      horizon:
        simulation.horizons.find((item) => item.horizon_h === horizonH) ??
        simulation.horizons[0] ??
        null,
      layerOptions,
      selectedProductCode,
    };
  },
);

export const selectFireSightMapViewModel = createSelector(
  selectFireSightActiveView,
  selectFireSightOverviewView,
  selectFireSightBootstrap,
  selectFireSightMonitoringAreas,
  selectFireSightIncidents,
  selectFireSightHistoricalIncidents,
  selectFireSightHistoryCentroidsVisible,
  selectFireSightSelectedIncidentUuid,
  selectFireSightSelectedObservation,
  selectFireSightSelectedSimulation,
  selectFireSightSelectedHorizonH,
  selectFireSightSimulationViewModel,
  (
    mode,
    overviewView,
    bootstrap,
    monitoringAreas,
    incidents,
    historicalIncidents,
    historyCentroidsVisible,
    selectedIncidentUuid,
    observation,
    simulation,
    selectedHorizonH,
    simulationViewModel,
  ): IFireSightMapViewModel => ({
    mode,
    overviewView,
    snapshot: bootstrap?.snapshot ?? null,
    monitoringAreas,
    incidents:
      overviewView === 'live'
        ? incidents
        : historyCentroidsVisible
          ? historicalIncidents
          : [],
    selectedIncidentUuid,
    observation,
    simulation,
    selectedHorizonH,
    selectedProductCode: simulationViewModel?.selectedProductCode ?? null,
  }),
);
